/* ══════════════════════════════════════════════════════════
   UNROLS — CART DRAWER
   Replaces assets/cart-drawer.js.

   One custom element owning the whole drawer through delegated
   listeners on itself, so nothing needs re-binding after a
   re-render — which matters, because every cart mutation
   replaces the drawer's entire inner HTML.

   Deliberately does NOT extend Dawn's CartItems (assets/cart.js).
   That class hunts for .js-contents / main-cart-footer / a
   <cart-items> table, none of which exist in this markup. The
   cart page already abandoned it too.

   Depends on, all loaded earlier and deferred so order holds:
     - global.js   → trapFocus / removeTrapFocus / fetchConfig,
                     and <slider-component>, which owns the LMA paging
     - pubsub.js   → publish
     - constants.js→ PUB_SUB_EVENTS
   ══════════════════════════════════════════════════════════ */

/* How long each "Last minute additions" card holds before the row advances.
   Declared out here rather than as a static class field, which Safari did not
   support until 16 — this file ships to shoppers, not to a build step. */
const LMA_AUTOPLAY_MS = 2000;

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends HTMLElement {
      constructor() {
        super();

        this.panel = this.querySelector('.unrols-drawer__panel');
        this.activeElement = null;

        // Delegated — bound to the element, not to its contents, so a
        // re-render of #CartDrawerContents cannot drop them.
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));

        this.onKeyUp = this.onKeyUp.bind(this);
      }

      connectedCallback() {
        this.bindHeaderIcon();
      }

      /* ── Header icon ──────────────────────────
         The <a href="/cart"> stays in the markup: with JS off it is
         still a working link to the cart page. Here we take the click
         over and open the drawer instead. */
      bindHeaderIcon() {
        const cartLink = document.querySelector('#cart-icon-bubble');
        if (!cartLink || cartLink.dataset.drawerBound === 'true') return;

        cartLink.dataset.drawerBound = 'true';
        cartLink.setAttribute('role', 'button');
        cartLink.setAttribute('aria-haspopup', 'dialog');
        cartLink.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(cartLink);
        });
      }

      /* ── Open / close ─────────────────────────── */

      open(triggeredBy) {
        if (triggeredBy) this.setActiveElement(triggeredBy);

        this.classList.add('active');
        document.body.classList.add('unrols-drawer-open');
        document.addEventListener('keyup', this.onKeyUp);

        // Wait for the panel to finish sliding in before trapping focus;
        // focusing mid-transition makes the browser scroll the moving
        // element into view and the panel visibly jumps.
        //
        // The timeout is a backstop: transitionend does not fire when the
        // drawer is already open (adding .active changes nothing to animate)
        // or when the viewer has reduced motion on. Whichever lands first
        // wins — the guard stops the loser from yanking focus back out of
        // wherever the shopper has since tabbed to.
        let focused = false;
        const focusPanel = () => {
          if (focused) return;
          focused = true;
          this.panel.removeEventListener('transitionend', focusPanel);
          trapFocus(this, this.panel);
        };

        this.panel.addEventListener('transitionend', focusPanel);
        setTimeout(focusPanel, 400);

        this.startAutoplay();
      }

      close() {
        this.classList.remove('active');
        document.body.classList.remove('unrols-drawer-open');
        document.removeEventListener('keyup', this.onKeyUp);
        removeTrapFocus(this.activeElement);
        this.activeElement = null;
        this.stopAutoplay();
      }

      onKeyUp(event) {
        if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
      }

      /* ── Delegated handlers ───────────────────── */

      onClick(event) {
        const closer = event.target.closest('[data-drawer-close]');
        if (closer) {
          // The empty-state "Start shopping" link carries data-drawer-close
          // too, but it is a real link — let it navigate.
          if (closer.tagName !== 'A') event.preventDefault();
          this.close();
          return;
        }

        const lineButton = event.target.closest('[data-cart-line]');
        if (lineButton) {
          event.preventDefault();
          this.changeLine(
            parseInt(lineButton.dataset.cartLine, 10),
            parseInt(lineButton.dataset.cartQty, 10)
          );
        }
      }

      onChange(event) {
        const select = event.target.closest('.unrols-lma__select');
        if (!select) return;

        // Add stays disabled until a size is actually chosen — the whole
        // point of the picker is that nothing is added blind.
        const addButton = select.form && select.form.querySelector('.unrols-lma__add');
        if (addButton) addButton.disabled = select.value === '';
      }

      /* ── "Last minute additions" autoplay ──────
         The row advances on its own every LMA_AUTOPLAY_MS.

         The timer only runs while the drawer is open: the panel is
         visibility:hidden the rest of the time, so ticking would move a row
         nobody can see and would leave the row mid-scroll on next open.

         Nothing here caches DOM references. Every cart mutation replaces the
         whole of #CartDrawerContents, so the slider, the track and the arrows
         are new elements afterwards — the tick re-queries instead. */

      startAutoplay() {
        this.stopAutoplay();

        // An auto-advancing carousel is squarely what this media query is for.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        this.autoplayTimer = setInterval(() => this.advanceLma(), LMA_AUTOPLAY_MS);
      }

      stopAutoplay() {
        if (!this.autoplayTimer) return;
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }

      advanceLma() {
        const lma = this.querySelector('.unrols-drawer__lma');
        const track = this.querySelector('#Slider-CartDrawerLMA');
        if (!lma || !track) return;

        // Skip the tick rather than cancel the timer: hovering or tabbing in
        // is a pause, not a stop, so it resumes on its own once they move off.
        // Covers an open size <select> too, which holds focus inside the row.
        if (lma.matches(':hover') || lma.contains(document.activeElement)) return;

        // Only rendered when there is more than one card (see the lma_count
        // check in snippets/cart-drawer.liquid) — with one card there is
        // nothing to advance to.
        const next = lma.querySelector('button[name="next"]');
        if (!next) return;

        // Drive slider-component's own button instead of recomputing the step.
        // It already measured the slide offset and it keeps the arrows'
        // disabled state in sync; duplicating that maths here would be a
        // second source of truth that drifts the moment the card width changes.
        // A disabled next button is how it reports "last card", so that is the
        // cue to wrap back to the first.
        if (next.hasAttribute('disabled')) {
          track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          next.click();
        }
      }

      /* ── Cart mutation ────────────────────────── */

      changeLine(line, quantity) {
        const row = this.querySelector(`.unrols-drawer__item[data-line="${line}"]`);
        if (row) row.classList.add('is-loading');

        const config = fetchConfig();
        config.body = JSON.stringify({
          line: line,
          quantity: quantity,
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        });

        // Passing `sections` means Shopify returns the re-rendered drawer in
        // this same response, so the line, the free-shipping bar, the total
        // and the suggestion row all move together — no second round trip,
        // and no page reload (which is what the cart page still does at
        // sections/main-cart.liquid:780).
        fetch(`${routes.cart_change_url}`, config)
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.status) {
              // Shopify rejected it — usually the requested quantity is no
              // longer in stock. Re-read the cart so the UI shows the truth.
              console.error('Cart change rejected:', parsedState.message || parsedState.description);
              if (row) row.classList.remove('is-loading');
              return;
            }

            this.updateSections(parsedState);

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-drawer',
              cartData: parsedState,
            });
          })
          .catch((error) => {
            console.error(error);
            if (row) row.classList.remove('is-loading');
          });
      }

      /* ── product-form.js contract ──────────────
         assets/product-form.js:11 picks this element up with
         document.querySelector('cart-drawer') and calls exactly these
         three. Every add-to-cart on the site routes through it, so the
         shapes below are what make quick-add and the PDP open this
         drawer without a line of their own. */

      getSectionsToRender() {
        return [
          {
            id: 'cart-drawer',
            source: '#CartDrawerContents',
            target: '#CartDrawerContents',
          },
          {
            id: 'cart-icon-bubble',
            source: '.shopify-section',
            target: '#cart-icon-bubble',
          },
        ];
      }

      setActiveElement(element) {
        this.activeElement = element;
      }

      renderContents(parsedState) {
        this.updateSections(parsedState);
        this.open();
      }

      /* ── Rendering ────────────────────────────── */

      updateSections(parsedState) {
        if (!parsedState.sections) return;

        this.getSectionsToRender().forEach((section) => {
          const target = document.querySelector(section.target);
          const html = parsedState.sections[section.id];
          if (!target || !html) return;

          const fragment = this.extract(html, section.source);
          if (fragment !== null) target.innerHTML = fragment;
        });

        // Only ever swaps the CONTENTS. Replacing <cart-drawer> itself would
        // orphan the `this.cart` reference every <product-form> on the page
        // captured in its constructor, and the next add would throw.
        this.classList.toggle('is-empty', this.querySelector('.unrols-drawer__empty') !== null);
      }

      extract(html, selector) {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const node = parsed.querySelector(selector);
        return node ? node.innerHTML : null;
      }
    }
  );
}
